/**
 * FilterPanel – Reusable filter trigger + dropdown panel.
 *
 * Usage:
 *   <FilterPanel
 *     filters={availableFilters}         // { id, label, icon (img src or MUI element), active }[]
 *     onFilterClick={(filterId) => {}}   // called when an option box is clicked
 *     onClearAll={() => {}}              // called when "Clear All" is clicked
 *     hasClearable={boolean}             // disables Clear All when nothing active
 *   />
 *
 * Props contract is intentionally minimal so the component remains screen-agnostic.
 * The parent owns all filter state; this component only renders and delegates.
 */

import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Close as CloseIcon } from '@mui/icons-material';
import deleteBinIcon from '../../assets/images/delete_bin.svg';
import './FilterPanel.scss';

const FilterIcon = ({ icon, filterId, className }) => {
  if (!icon) return null;

  const iconClassName = `${className} fp-filter-icon fp-filter-icon--${filterId}`;

  if (typeof icon === 'string') {
    return (
      <span
        className={iconClassName}
        style={{ '--fp-icon-url': `url("${icon}")` }}
        aria-hidden="true"
      />
    );
  }

  return React.cloneElement(icon, {
    className: [icon.props?.className, iconClassName].filter(Boolean).join(' '),
  });
};

FilterIcon.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  filterId: PropTypes.string.isRequired,
  className: PropTypes.string,
};

// ─── FilterOptionBox ──────────────────────────────────────────────────────────
const FilterOptionBox = React.memo(({ filter, onClick }) => (
  <button
    type="button"
    className={`fp-option fp-option--${filter.id}${filter.active ? ' fp-option--active' : ''}`}
    onClick={() => onClick(filter.id)}
    aria-pressed={filter.active}
    title={filter.label}
  >
    {filter.icon && (
      <FilterIcon icon={filter.icon} filterId={filter.id} className="fp-option__icon" />
    )}
    <span className="fp-option__name">{filter.label}</span>
  </button>
));

FilterOptionBox.displayName = 'FilterOptionBox';

FilterOptionBox.propTypes = {
  filter: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    active: PropTypes.bool,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

// ─── FilterPanel ──────────────────────────────────────────────────────────────
const FilterPanel = ({
  filters = [],
  onFilterClick,
  onClearAll,
  onRemoveFilter,
  hasClearable = false,
  active = false,
  appliedCount = 0,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [panelPos, setPanelPos] = React.useState({ top: 0, left: 0 });
  const anchorRef = useRef(null);
  const triggerRef = useRef(null);

  const openPanel = React.useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const panelWidth = 420;
      // Prevent the panel from going off the right edge of the viewport
      const leftPos = Math.min(rect.left, window.innerWidth - panelWidth - 16);
      setPanelPos({ top: rect.bottom + 6, left: Math.max(16, leftPos) });
    }
    setOpen(true);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (
        anchorRef.current && !anchorRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        !e.target.closest('.MuiPopover-root') && 
        !e.target.closest('.MuiMenu-root') &&
        !e.target.closest('.MuiAutocomplete-popper') &&
        !e.target.closest('.MuiDialog-root') &&
        !e.target.closest('.MuiPopper-root')
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const handleFilterClick = useCallback((filterId) => {
    onFilterClick?.(filterId);
  }, [onFilterClick]);

  const handleClearAll = useCallback(() => {
    onClearAll?.();
  }, [onClearAll]);

  const handleRemoveFilter = useCallback((filterId) => {
    onRemoveFilter?.(filterId);
  }, [onRemoveFilter]);

  const visibleFilters = filters.filter((f) => f.visible);

  return (
    <div className="fp-anchor" ref={anchorRef}>
      {/* Trigger */}
      <button
        type="button"
        ref={triggerRef}
        className={`fp-trigger${active ? ' fp-trigger--active' : ''}`}
        onClick={() => {
          if (open) {
            setOpen(false);
          } else {
            openPanel();
          }
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={t('dashboard.filters', 'Filters')}
        title={t('dashboard.filters', 'Filters')}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 13 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M11.8333 0.5H1.16667C0.989856 0.5 0.820286 0.570238 0.695262 0.695262C0.570238 0.820286 0.5 0.989856 0.5 1.16667V2.224C0.500038 2.4008 0.5703 2.57034 0.695333 2.69533L4.97133 6.97133C5.09637 7.09633 5.16663 7.26587 5.16667 7.44267V11.3127C5.16667 11.4613 5.21634 11.6056 5.30777 11.7228C5.39921 11.84 5.52717 11.9232 5.67133 11.9593L7.00467 12.2927C7.10297 12.3173 7.20559 12.3192 7.30473 12.2982C7.40387 12.2772 7.49692 12.2339 7.57681 12.1716C7.6567 12.1092 7.72133 12.0295 7.76578 11.9384C7.81023 11.8473 7.83333 11.7473 7.83333 11.646V7.44267C7.83337 7.26587 7.90363 7.09633 8.02867 6.97133L12.3047 2.69533C12.4297 2.57034 12.5 2.4008 12.5 2.224V1.16667C12.5 0.989856 12.4298 0.820286 12.3047 0.695262C12.1797 0.570238 12.0101 0.5 11.8333 0.5Z"
            stroke="currentColor"
            strokeWidth="1"
            fill={active ? 'currentColor' : 'none'}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {appliedCount > 0 && (
          <span className="fp-trigger__badge">
            {appliedCount}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fp-panel custom-scrollbar"
          role="dialog"
          aria-label={t('dashboard.filters', 'Filters')}
          ref={anchorRef}
          style={{
            top: panelPos.top,
            left: panelPos.left,
            maxHeight: `calc(100vh - ${panelPos.top + 16}px)`
          }}
        >
          {/* Header */}
          <div className="fp-panel__header">
            <button
              type="button"
              className="fp-btn-close"
              onClick={() => {
                setOpen(false);
              }}
              title={t('common.close', 'Close')}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
            <button
              type="button"
              className="fp-btn-clear"
              onClick={handleClearAll}
              disabled={!hasClearable}
            >
              {t('dashboard.clearAll', 'Clear All')}
            </button>
          </div>

          {/* Separator */}
          <div className="fp-panel__separator" aria-hidden="true" />

          {/* Filter Options Grid */}
          {filters.length > 0 ? (
            <div className="fp-options" role="group" aria-label={t('dashboard.filterOptions', 'Filter options')}>
              {filters.map((filter) => (
                <FilterOptionBox
                  key={filter.id}
                  filter={filter}
                  onClick={handleFilterClick}
                />
              ))}
            </div>
          ) : null}

          {/* Selected Filters Section */}
          {visibleFilters.length > 0 && (
            <>
              <div className="fp-selected-separator" aria-hidden="true" />
              <div className="fp-selected-list">
                {visibleFilters.map((filter) => (
                  (() => {
                    const dropdown = filter.renderDropdown?.();

                    return (
                      <div key={filter.id} className="fp-selected-row">
                        <div className="fp-selected-label">
                          {filter.icon && (
                            <FilterIcon icon={filter.icon} filterId={filter.id} className="fp-selected-icon" />
                          )}
                          <span className="fp-selected-name">{filter.label}</span>
                        </div>
                        <div className="fp-selected-dropdown">
                          <div
                            className="fp-selected-dropdown-anchor"
                            data-filter-id={filter.id}
                          >
                            {dropdown}
                          </div>
                        </div>
                        {onRemoveFilter && (
                          <button
                            type="button"
                            className="fp-selected-delete"
                            onClick={() => handleRemoveFilter(filter.id)}
                            title={t('dashboard.removeFilter', 'Remove filter')}
                            disabled={filter.disableRemove}
                          >
                            <img src={deleteBinIcon} alt="Delete" />
                          </button>
                        )}
                      </div>
                    );
                  })()
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

FilterPanel.propTypes = {
  /** Ordered list of filter descriptors. Order is preserved in the grid. */
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      /** Img src string OR a React element (e.g. MUI icon). */
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      /** Whether this filter is currently active/selected. */
      active: PropTypes.bool,
      /** Whether this filter is currently visible in the selected filters section. */
      visible: PropTypes.bool,
      /** Function returning the dropdown React node. */
      renderDropdown: PropTypes.func,
    })
  ),
  /** Called with the filter id when an option box is clicked. */
  onFilterClick: PropTypes.func,
  /** Called when "Clear All" button is clicked. */
  onClearAll: PropTypes.func,
  /** Called when delete bin is clicked for a selected filter row. */
  onRemoveFilter: PropTypes.func,
  /** Whether any filter is active (enables the Clear All button). */
  hasClearable: PropTypes.bool,
  /** Whether any filter is applied (toggles filled state on the funnel icon). */
  active: PropTypes.bool,
  /** Number of applied filters. */
  appliedCount: PropTypes.number,
};

export default FilterPanel;
